/**
 * ModifiersControls component - Shift and Alt modifier buttons for sidebar
 * Factory function following the app's architectural patterns
 * 
 * KNOWN LIMITATION: On iPad Safari, holding a modifier button with a finger
 * while drawing with Apple Pencil does NOT work. Safari blocks pencil input
 * when a finger touch is active. This is a fundamental browser limitation.
 * 
 * Alternative approach: Use floating menus on press-and-hold gestures to
 * create standard shapes (ellipses, straight lines, rectangles).
 */

import { createIconButton, type IconButtonAPI } from "@UI/IconButton/index.js";

export interface ModifiersControlsConfig {
  readonly onShiftToggle?: (active: boolean) => void;
  readonly onAltToggle?: (active: boolean) => void;
  readonly initialShift?: boolean;
  readonly initialAlt?: boolean;
}

export interface ModifiersControlsAPI {
  readonly getElement: () => HTMLElement;
  readonly setShiftActive: (active: boolean) => void;
  readonly setAltActive: (active: boolean) => void;
  readonly getShiftActive: () => boolean;
  readonly getAltActive: () => boolean;
  readonly destroy: () => void;
}

/**
 * Creates ModifiersControls component with shift and alt toggle buttons
 * @param config - Configuration for the modifiers controls
 * @returns ModifiersControls API
 */
export const createModifiersControls = (
  config: ModifiersControlsConfig = {}
): ModifiersControlsAPI => {
  const {
    onShiftToggle,
    onAltToggle,
    initialShift = false,
    initialAlt = false,
  } = config;

  let isDestroyed = false;
  let isShiftActive = initialShift;
  let isAltActive = initialAlt;
  let shiftButtonAPI: IconButtonAPI | null = null;
  let altButtonAPI: IconButtonAPI | null = null;

  // Create container element
  const container = document.createElement("div");
  container.className = "modifiers-controls";

  /**
   * Initialize the modifiers controls with two IconButtons
   */
  const initModifiersControls = (): void => {
    try {
      // Create shift button (momentary behavior)
      shiftButtonAPI = createIconButton({
        icon: "modifiers_shift",
        size: 44,
        iconSize: 28,
        behavior: "momentary",
        selected: isShiftActive,
        className: "modifiers-button modifiers-button--shift",
        onPointerDown: () => {
          if (isDestroyed) return;
          isShiftActive = true;
          onShiftToggle?.(true);
        },
        onPointerUp: () => {
          if (isDestroyed) return;
          isShiftActive = false;
          onShiftToggle?.(false);
        },
      });

      // Create alt button (momentary behavior)
      altButtonAPI = createIconButton({
        icon: "modifiers_alt",
        size: 44,
        iconSize: 28,
        behavior: "momentary",
        selected: isAltActive,
        className: "modifiers-button modifiers-button--alt",
        onPointerDown: () => {
          if (isDestroyed) return;
          isAltActive = true;
          onAltToggle?.(true);
        },
        onPointerUp: () => {
          if (isDestroyed) return;
          isAltActive = false;
          onAltToggle?.(false);
        },
      });

      // Append buttons to container
      container.appendChild(shiftButtonAPI.getElement());
      container.appendChild(altButtonAPI.getElement());
    } catch (error) {
      console.error("Failed to create ModifiersControls component:", error);
      throw error;
    }
  };

  /**
   * Set shift active state
   */
  const setShiftActive = (active: boolean): void => {
    if (isDestroyed || !shiftButtonAPI) return;

    isShiftActive = active;
    shiftButtonAPI.setSelected(active);
  };

  /**
   * Set alt active state
   */
  const setAltActive = (active: boolean): void => {
    if (isDestroyed || !altButtonAPI) return;

    isAltActive = active;
    altButtonAPI.setSelected(active);
  };

  /**
   * Get current shift active state
   */
  const getShiftActive = (): boolean => {
    return isShiftActive;
  };

  /**
   * Get current alt active state
   */
  const getAltActive = (): boolean => {
    return isAltActive;
  };

  /**
   * Clean up resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    // Destroy button components
    shiftButtonAPI?.destroy();
    altButtonAPI?.destroy();

    // Clear references
    shiftButtonAPI = null;
    altButtonAPI = null;

    // Remove from DOM if still connected
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  };

  // Initialize the component
  initModifiersControls();

  return {
    getElement: () => container,
    setShiftActive,
    setAltActive,
    getShiftActive,
    getAltActive,
    destroy,
  };
};

/**
 * Creates ModifiersControls component and appends to container
 * @param container - Container element to append to
 * @param config - Configuration for the modifiers controls
 * @returns ModifiersControls API
 */
export const createModifiersControlsInContainer = (
  container: HTMLElement,
  config: ModifiersControlsConfig = {}
): ModifiersControlsAPI => {
  const modifiersControls = createModifiersControls(config);
  container.appendChild(modifiersControls.getElement());
  return modifiersControls;
};
